import { useReducer, useEffect, useMemo, createContext } from 'react';
import type {
  WizardContextValue,
  WizardProviderProps,
  WizardState,
  WizardAction,
  WizardActions,
  WizardValuesBase,
} from './types';

const wizardReducer = <T extends WizardValuesBase>(
  state: WizardState<T>,
  action: WizardAction<T>
): WizardState<T> => {
  switch (action.type) {
    case 'Navigate':
      return {
        ...state,
        currentStepId: action.payload,
      };

    case 'UpdateValues':
      return {
        ...state,
        values: {
          ...state.values,
          ...action.payload,
        },
      };

    case 'Init':
      return action.payload;

    default:
      return state;
  }
};

// eslint-disable-next-line react-refresh/only-export-components
export const WizardContext =
  createContext<WizardContextValue<WizardValuesBase> | null>(null);

export const WizardProvider = <T extends WizardValuesBase = WizardValuesBase>({
  children,
  initialState,
}: WizardProviderProps<T>) => {
  const [state, dispatch] = useReducer(
    wizardReducer<T>,
    initialState as WizardState<T>
  );

  const serializedInitialState = JSON.stringify(initialState);
  useEffect(() => {
    dispatch({
      type: 'Init',
      payload: initialState as WizardState<T>,
    });
  }, [serializedInitialState, initialState]);

  const defaultActions: WizardActions<T> = useMemo(
    () => ({
      navigate: (stepId: string) => {
        dispatch({
          type: 'Navigate',
          payload: stepId,
        });
      },
      updateValues: (values: Partial<T>) => {
        dispatch({
          type: 'UpdateValues',
          payload: values,
        });
      },
    }),
    []
  );

  const actions = initialState.actions ?? defaultActions;

  const contextValue: WizardContextValue<T> = useMemo(
    () => ({ state, actions }),
    [state, actions]
  );

  return (
    <WizardContext.Provider
      value={contextValue as WizardContextValue<WizardValuesBase>}
    >
      {children}
    </WizardContext.Provider>
  );
};
